import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { LANGUAGES, setLanguage, type Language } from '../i18n';
import { useTheme } from '../theme/ThemeContext';
import { Button } from '../components/Button';
import { ExpiryWarning } from '../features/auth/ExpiryWarning';
import { useAuth } from '../features/auth/AuthContext';
import styles from './Layout.module.css';

/** Einträge der Hauptnavigation (Adresse, Übersetzungsschlüssel, genauer Treffer). */
const NAV_ITEMS = [
  { to: '/', label: 'nav.home', end: true },
  { to: '/competitions', label: 'nav.competitions', end: true },
  { to: '/competitions/new', label: 'nav.salons', end: false },
  { to: '/images', label: 'nav.images', end: false },
  { to: '/export', label: 'nav.export', end: false },
  { to: '/import', label: 'nav.import', end: false },
  { to: '/einstellungen', label: 'nav.settings', end: false },
] as const;

/**
 * Rahmen der Anwendung: Kopfzeile mit Navigation (je nach Rolle), Sprachwahl, Theme-Umschalter und
 * Abmelden, Inhaltsbereich (`Outlet`) und Fußzeile. Bei erzwungenem Passwortwechsel wird die
 * Navigation ausgeblendet.
 */
export function Layout() {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const showNav = Boolean(user && !user.mustChangePassword);
  /** Klasse eines Navigationslinks: hervorgehoben, wenn die Seite aktiv ist. */
  const linkClass = ({ isActive }: { isActive: boolean }) => (isActive ? styles.active : undefined);

  /** Meldet ab und führt zur Anmeldeseite. */
  async function handleLogout(): Promise<void> {
    await logout();
    navigate('/login');
  }

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <span className={styles.brand}>{t('app.name')}</span>
        {showNav && (
          <nav aria-label={t('nav.main')} className={styles.nav}>
            <ul className={styles.navList}>
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink to={item.to} end={item.end} className={linkClass}>
                    {t(item.label)}
                  </NavLink>
                </li>
              ))}
              {user?.role === 'admin' && (
                <li>
                  <NavLink to="/admin" className={linkClass}>
                    {t('nav.admin')}
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>
        )}
        {!showNav && <span className={styles.nav} />}
        <div className={styles.tools}>
          {user && (
            <span className={styles.user}>{t('auth.loggedInAs', { name: user.username })}</span>
          )}
          <label className={styles.langLabel}>
            <span className={styles.visuallyHidden}>{t('lang.label')}</span>
            <select
              className={styles.langSelect}
              value={i18n.language}
              onChange={(event) => void setLanguage(event.target.value as Language)}
            >
              {LANGUAGES.map((language) => (
                <option key={language} value={language}>
                  {t(`lang.${language}`)}
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="secondary"
            className={styles.iconButton}
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
            title={theme === 'dark' ? t('theme.toLight') : t('theme.toDark')}
          >
            <span aria-hidden="true">{theme === 'dark' ? '☀' : '☾'}</span>
          </Button>
          {user && (
            <Button
              variant="secondary"
              className={styles.compact}
              onClick={() => void handleLogout()}
            >
              {t('auth.logout')}
            </Button>
          )}
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
      <footer className={styles.footer}>{t('app.footer')}</footer>
      <ExpiryWarning />
    </div>
  );
}
